import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, ChevronLeft, ListChecks, X } from 'lucide-react';
import type { ReactNode, Ref } from 'react';

import { useIsMobile, useMediaQuery, useOverlayParam } from '@/shared/mobile';
import { PageHeader } from '@/shared/patterns';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/shared/ui/drawer';

import { FlowRail } from './flow-rail';
import { FlowSummaryView } from './flow-summary';
import type { FlowTrailStep } from './flow-trail';

/** Chave do sheet "Suas escolhas" do iPhone na URL (`?sheet=escolhas`): o voltar fecha. */
export const FLOW_CHOICES_SHEET = 'escolhas';

const WIDE_QUERY = '(min-width: 1280px)';

export interface FlowMobileHeaderProps {
  /** "Contratar · Consignado público". */
  readonly title: string;
  readonly position: number;
  readonly total: number;
  readonly stepTitle: string;
  readonly headingId?: string;
  readonly headingRef?: Ref<HTMLHeadingElement>;
  /** O botão do cabeçalho: cancelar (X) no primeiro passo, voltar (‹) nos outros. */
  readonly nav?: { readonly kind: 'cancel' | 'back'; readonly label: string; readonly onClick: () => void; readonly disabled?: boolean } | undefined;
}

/** Cabeçalho compacto do fluxo no iPhone: "<título> · Passo n de N", o nome da etapa e a barra fina (U12). */
export function FlowMobileHeader({ title, position, total, stepTitle, headingId, headingRef, nav }: FlowMobileHeaderProps) {
  const { t } = useLingui();
  return (
    <header className="flex flex-col gap-2">
      <div className="flex items-center gap-1">
        {nav ? (
          <Button type="button" variant="ghost" size="icon" className="-ml-2 shrink-0" aria-label={nav.label} disabled={nav.disabled} onClick={nav.onClick}>
            {nav.kind === 'cancel' ? <X aria-hidden /> : <ChevronLeft aria-hidden />}
          </Button>
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col">
          <h1 className="truncate text-xs text-muted-foreground">
            {title} · {t`Passo ${position} de ${total}`}
          </h1>
          <h2 id={headingId} ref={headingRef} tabIndex={-1} className="truncate text-lg font-semibold text-foreground outline-none">
            {stepTitle}
          </h2>
        </div>
      </div>
      <span aria-hidden className="block h-1 overflow-hidden rounded-full bg-input">
        <span className="block h-full origin-left rounded-full bg-primary transition-transform duration-(--lume-dur-enter) ease-(--lume-ease-out)" style={{ transform: `scaleX(${String(position / total)})` }} />
      </span>
    </header>
  );
}

export interface FlowStepHeadProps {
  /** "Etapa n de N". */
  readonly eyebrow?: string | undefined;
  readonly title: string;
  readonly description?: ReactNode;
  readonly headingId?: string | undefined;
  readonly headingRef?: Ref<HTMLHeadingElement> | undefined;
}

/** O topo da etapa na web (U53): "Etapa n de N", o título (`h2`, recebe o foco na troca) e a frase do que se decide. */
export function FlowStepHead({ eyebrow, title, description, headingId, headingRef }: FlowStepHeadProps) {
  return (
    <div className="flex flex-col gap-1">
      {eyebrow ? <span className="font-mono text-xs tracking-wide text-muted-foreground uppercase">{eyebrow}</span> : null}
      <h2 id={headingId} ref={headingRef} tabIndex={-1} className="text-xl font-semibold text-foreground outline-none">
        {title}
      </h2>
      {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
    </div>
  );
}

export interface FlowChoiceBarProps {
  /** O que falta para seguir ("Escolha um card para seguir."). */
  readonly hint: string;
  readonly back?: { readonly label: string; readonly onClick: () => void } | undefined;
}

/**
 * A barra de uma etapa de escolha sem botão principal (o card avança, W07): Voltar à esquerda e o que falta à direita,
 * presa embaixo como a do `FlowScreen`.
 */
export function FlowChoiceBar({ hint, back }: FlowChoiceBarProps) {
  return (
    <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-2 border-t border-border bg-background/95 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur">
      {back ? (
        <Button type="button" variant="secondary" onClick={back.onClick}>
          <ArrowLeft aria-hidden />
          {back.label}
        </Button>
      ) : null}
      <span className="flex-1" />
      <span className="text-sm text-muted-foreground">{hint}</span>
    </div>
  );
}

export interface FlowFrameProps {
  /** Título da página: "Contratar · {produto}" (W10). */
  readonly title: string;
  readonly description?: ReactNode;
  readonly back?: { readonly label: string; readonly onClick: () => void } | undefined;
  readonly trail: readonly FlowTrailStep[];
  /** Liga o painel "Suas escolhas" do próprio trilho. */
  readonly summary?: boolean;
  /** Outro conteúdo no lugar do "Suas escolhas" (sem `summary`). */
  readonly aside?: ReactNode;
  /** Aviso do painel ("Trocar o produto limpou as escolhas seguintes"). */
  readonly summaryNote?: ReactNode;
  /** Cabeçalho do iPhone (`FlowMobileHeader`); a web usa o `PageHeader`. */
  readonly mobileHeader: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}

/**
 * Anatomia do Fluxo guiado (U53, discovery/13 §3): trilho vertical à esquerda, a etapa no meio e "Suas escolhas"
 * fixo à direita a partir de 1280 px; entre 768 e 1280 px o painel vira a faixa de chips no topo da etapa; no
 * iPhone, o cabeçalho compacto e o chip "Suas escolhas · n", que abre o sheet em `?sheet=escolhas`.
 */
export function FlowFrame({ title, description, back, trail, summary = false, aside: custom, summaryNote, mobileHeader, children, className }: FlowFrameProps) {
  const { t } = useLingui();
  const isMobile = useIsMobile();
  const wide = useMediaQuery(WIDE_QUERY);
  const [sheetOpen, setSheetOpen] = useOverlayParam(FLOW_CHOICES_SHEET);
  const aside = summary || (custom !== undefined && custom !== null && custom !== false);
  const choices = trail.filter((step) => !step.review);
  const decided = choices.filter((step) => step.state !== 'pending' && step.items.length > 0).length;

  if (isMobile)
    return (
      <div data-slot="flow-frame" className={cn('flex min-w-0 flex-col gap-4', className)}>
        {mobileHeader}
        {aside ? (
          <Button type="button" variant="secondary" size="sm" className="self-start rounded-full" onClick={() => setSheetOpen(true)}>
            <ListChecks aria-hidden />
            {summary ? t`Suas escolhas · ${decided}` : t`Suas escolhas`}
          </Button>
        ) : null}
        {children}
        {aside ? (
          <Drawer open={sheetOpen} onOpenChange={setSheetOpen}>
            <DrawerContent>
              <DrawerHeader>
                <DrawerTitle>{t`Suas escolhas`}</DrawerTitle>
                <DrawerDescription>{t`${decided} de ${choices.length} etapas decididas`}</DrawerDescription>
              </DrawerHeader>
              <DrawerBody className="pb-6">
                {summary ? <FlowSummaryView steps={trail} heading={false} note={summaryNote} onBeforeSelect={() => setSheetOpen(false)} /> : custom}
              </DrawerBody>
            </DrawerContent>
          </Drawer>
        ) : null}
      </div>
    );

  const panel = summary ? <FlowSummaryView steps={trail} note={summaryNote} className="rounded-card border border-border bg-card p-4" /> : custom;
  return (
    <div data-slot="flow-frame" className={cn('flex min-w-0 flex-col gap-5', className)}>
      <PageHeader title={title} description={description} {...(back ? { back } : {})} />
      {/* `xl` (1280 px) e não `min-[1280px]`: a variante arbitrária sai antes do `md` no CSS e perde para ele. */}
      <div className={cn('grid min-w-0 items-start gap-6 md:grid-cols-[12.5rem_minmax(0,1fr)]', aside && 'xl:grid-cols-[12.5rem_minmax(0,1fr)_17.5rem]')}>
        <FlowRail steps={trail} className="md:sticky md:top-4" />
        <div className="flex min-w-0 flex-col gap-5">
          {aside && !wide ? summary ? <FlowSummaryView steps={trail} variant="strip" /> : custom : null}
          {children}
        </div>
        {aside && wide ? (
          // `div`, não `aside`: o fluxo mora no `<main>` da casca, e o complementar dentro dele reprova no axe.
          <div data-slot="flow-aside" className="sticky top-4">
            {panel}
          </div>
        ) : null}
      </div>
    </div>
  );
}
